import React from 'react'
import './style/v2-app-menu.scss'

import classnames from 'classnames'

import Icon, {
    AppstoreFilled,
    AreaChartOutlined,
    BuildFilled,
    SelectOutlined,
    SettingFilled,
    ShoppingOutlined,
    TeamOutlined,
    WifiOutlined,
} from '@ant-design/icons'
import { FiRadio } from 'react-icons/fi'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { asCaslSubject, getDemoAttributesFor, getOrgDemoAttributesFor } from '../../stores/app-ability'
import AppMessageFilled from '../icons/app-message-filled'
import { useService } from '@pushly/aqe/lib/hooks'
import { FEAT_MULTI_DOMAIN_NOTIFICATIONS } from '../../constants'
import { getSidebarPathModule } from './helpers'
import { FEAT_APP_MESSAGES } from '../../constants'
import { FEAT_FEEDS_MGMT } from '@pushly/aqe/lib/constants'
import { preventDefault } from '@pushly/aqe/lib/utils'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core'
import { Menu, MenuProps } from 'antd'
import { AntdClassnameTokens } from '../../styles/antd-classname-tokens'
import { shouldShowCatalogNavItem } from '../../features/catalog/helpers'

type ItemType = NonNullable<MenuProps['items']>[number]

const appMenuKeyMap: Record<string, string> = {
    dashboard: '/dashboard',
    notifications: '/notifications',
    feeds: '/feeds',
    appMessages: '/app-messages',
    journeys: '/journeys',
    catalogs: '/catalogs',
    segments: '/segments',
    prompts: '/prompts',
    insights: '/insights',
    settings: '/#settings',
}

const AppMenu = () => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const userPreferences = useUserPreferences(appState.currentUser?.id)

    const buildMultiTierCondition = (domainEntity: SubjectEntity, orgEntity: SubjectEntity, orgFlag?) => {
        return () => {
            const entity = appState.inDomainContext ? domainEntity : orgEntity
            const abilityConstraints = appState.inDomainContext
                ? {
                      domainId: appState.currentDomain?.id,
                      ...getDemoAttributesFor(appState.currentDomain?.id, appState.currentDomain),
                  }
                : {
                      accountId: appState.currentDomain?.accountId,
                      ...getOrgDemoAttributesFor(appState.currentDomain?.accountId, appState.currentDomain),
                  }

            /**
             * Menu Visibility:
             * Domain Context  - user has read access to domain
             * Org Context     - org has key & user has read access to org
             */
            let can = appState.abilityStore.can(AbilityAction.READ, asCaslSubject(entity, abilityConstraints))
            if (can && appState.inOrgContext && orgFlag) {
                can = appState.currentDomain?.accountFlags?.includes(orgFlag) ?? false
            }

            return can ?? false
        }
    }

    const inOrgContext = appState.inOrgContext
    const routeEntity = inOrgContext ? 'org' : 'domain'

    const selectedPathKey = getSidebarPathModule(location.pathname)

    // access observable selected domain/account in render fn to ensure redraw
    const activeAcctId = appState.currentDomain?.accountId ?? 0
    const activeDomainId = appState.currentDomain?.id ?? 0

    if (appState?.abilityStore?.acs?.state && !appState.abilityStore.abilities.rules.length) {
        Promise.resolve(appState.abilityStore.acs.getAbilities())
    }

    const getMenuItems = (configs): ItemType[] => {
        const menuItems: ItemType[] = []

        configs.forEach((config) => {
            const menuItem: ItemType = {
                key: config.key,
                disabled: !appState.currentDomain?.id,
                icon: config.icon,
                // title: config.title,
                label: config.title,
            }

            let canDisplay: boolean = true
            if (config.condition) {
                canDisplay = config.condition()
            }

            if (canDisplay) {
                menuItems.push(menuItem)
            }
        })

        return menuItems
    }

    return (
        <Menu
            className={classnames(
                'app-menu',
                `active-account-${activeAcctId}`,
                `active-domain-${activeDomainId}`,
                appState.abilityStore.acs?.state,
                {
                    collapsed: !userPreferences.get(['sidebar_expanded']),
                    open: userPreferences.get(['sidebar_expanded']),
                },
            )}
            theme="dark"
            mode="inline"
            inlineCollapsed={!userPreferences.get(['sidebar_expanded'])}
            selectedKeys={!selectedPathKey ? [] : [selectedPathKey]}
            onClick={({ key: itemKey, domEvent }) => {
                preventDefault(domEvent)

                const currentPath = appService.currentRoute
                const newPath = appMenuKeyMap[itemKey]

                if ((domEvent as any).metaKey) {
                    window.open(appService.routeWithin(routeEntity, newPath, true))
                } else if (currentPath !== newPath) {
                    appService.routeWithin(routeEntity, newPath)
                }
            }}
            classNames={AntdClassnameTokens.Menu({ prefix: 'sidebar' })}
            items={getMenuItems([
                {
                    key: 'dashboard',
                    disabled: !appState.currentDomain?.id,
                    icon: <AppstoreFilled aria-hidden="true" />,
                    title: 'Dashboard',
                    condition() {
                        const entity = appState.inDomainContext
                            ? appState.abilityStore.currentDomainIdentity
                            : appState.abilityStore.currentOrgIdentity

                        const canRead = appState.abilityStore.can(AbilityAction.READ, entity)

                        return canRead
                    },
                },
                {
                    key: 'notifications',
                    disabled: !appState.currentDomain?.id,
                    icon: <WifiOutlined aria-hidden="true" />,
                    title: 'Notifications',
                    condition: buildMultiTierCondition(
                        SubjectEntity.NOTIFICATION,
                        SubjectEntity.ORG_NOTIFICATION,
                        FEAT_MULTI_DOMAIN_NOTIFICATIONS,
                    ),
                },
                {
                    key: 'appMessages',
                    disabled: !appState.currentDomain?.id,
                    icon: <Icon component={AppMessageFilled} aria-hidden="true" />,
                    title: 'App Messages',
                    condition() {
                        return (
                            (appState.currentDomain?.flags.includes(FEAT_APP_MESSAGES) ?? false) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.APP_MESSAGE),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'journeys',
                    disabled: !appState.currentDomain?.id,
                    title: 'Journeys',
                    icon: <BuildFilled aria-hidden="true" />,
                    condition() {
                        return (
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.CAMPAIGN),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'feeds',
                    disabled: !appState.currentDomain?.id,
                    icon: <Icon component={(props: any) => <FiRadio {...props} fill="none" />} aria-hidden="true" />,
                    title: 'Feeds',
                    condition() {
                        return (
                            (appState.currentDomain?.flags.includes(FEAT_FEEDS_MGMT) ?? false) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.FEED),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'catalogs',
                    disabled: !appState.currentDomain?.id,
                    icon: <ShoppingOutlined />,
                    get title() {
                        const itemType = appState.currentDomain?.ecommConfig?.itemType
                        const label = itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1) : 'Item'
                        return `${label} Catalog`
                    },
                    condition() {
                        return (
                            shouldShowCatalogNavItem(appState.currentDomain ?? null) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                /* Org Segments will not have a separate flag for multi-domain level.
                    If an org has access to multi-domain notifications they will
                    also implicitly have access to segmentation.
                */
                {
                    key: 'segments',
                    disabled: !appState.currentDomain?.id,
                    icon: <TeamOutlined aria-hidden="true" />,
                    title: 'Segments',
                    condition: buildMultiTierCondition(
                        SubjectEntity.SEGMENT,
                        SubjectEntity.ORG_SEGMENT,
                        FEAT_MULTI_DOMAIN_NOTIFICATIONS,
                    ),
                },
                {
                    key: 'prompts',
                    disabled: !appState.currentDomain?.id,
                    icon: <SelectOutlined aria-hidden="true" />,
                    title: 'Prompts',
                    condition() {
                        return (
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.PROMPT),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'insights',
                    disabled: !appState.currentDomain?.id,
                    icon: <AreaChartOutlined aria-hidden="true" />,
                    title: 'Insights',
                    condition() {
                        const entity = appState.inDomainContext
                            ? appState.abilityStore.currentDomainIdentity
                            : appState.abilityStore.currentOrgIdentity

                        return appState.abilityStore.can(AbilityAction.READ, entity)
                    },
                },
                {
                    key: 'settings',
                    disabled: !appState.currentDomain?.id,
                    icon: <SettingFilled aria-hidden="true" />,
                    title: 'Settings',
                    condition() {
                        const entity = appState.inDomainContext
                            ? appState.abilityStore.currentDomainIdentity
                            : appState.abilityStore.currentOrgIdentity

                        return appState.abilityStore.can(AbilityAction.UPDATE, entity)
                    },
                },
            ])}
        />
    )
}

export default AppMenu
